import type { Leave } from '@lms/shared-types'

import { apiRequest, apiRequestPaged, type PagedResult } from './client'

/** One row of the HR-first-review queue (leaves in `pending_hr_review`). */
export interface HrReviewRow {
  id: string
  employeeId: string
  employeeName: string
  type: string
  startDate: string
  endDate: string
  reason: string | null
  /** How many supporting documents the requester has attached so far. */
  documentCount: number
  createdAt: string
}

/**
 * The HR review queue, numbered-pagination path. Passing `page` flips the route
 * to the enveloped `OkPaged[HrReviewRow]` shape, read via `apiRequestPaged` so
 * `meta.total` drives `usePagedList`. Oldest-first (longest-waiting on top).
 */
export function listHrReviewsPage(params: {
  page: number
  limit?: number
}): Promise<PagedResult<HrReviewRow>> {
  const qs = new URLSearchParams()
  qs.set('page', String(params.page))
  if (params.limit != null) qs.set('limit', String(params.limit))
  return apiRequestPaged<HrReviewRow>(`/api/v1/hr/leave-reviews?${qs.toString()}`)
}

/** Forward a reviewed leave into the normal manager flow (→ pending). */
export function forwardLeaveReview(id: string): Promise<Leave> {
  return apiRequest<Leave>(
    `/api/v1/hr/leave-reviews/${encodeURIComponent(id)}/forward`,
    { method: 'POST' },
  )
}

/** Reject a leave at the HR review stage (→ rejected). Reason required. */
export function rejectLeaveReview(id: string, reason: string): Promise<Leave> {
  return apiRequest<Leave>(
    `/api/v1/hr/leave-reviews/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body: { reason } },
  )
}

/** Ask the requester to attach/replace the document — stays in the queue. */
export function requestLeaveDocument(id: string, note: string): Promise<Leave> {
  return apiRequest<Leave>(
    `/api/v1/hr/leave-reviews/${encodeURIComponent(id)}/request-document`,
    { method: 'POST', body: { note } },
  )
}
